import { ColumnDetails } from '../../../grid/types/common';
import {
  ActivePolicy,
  AssignedPolicy,
  ClientPolicy,
  ClientRule,
  EndpointUsers,
  ProtectedEntity,
  Specifics,
} from '@nirvana/shared/dtos/types/protected-entities/common';
import { Filter as GridFilter } from '../../../grid/gridFilters/types';
import { GridFilterUiConfiguration } from '../../../types/filters/types';
import { NetworkPopUpType } from '../../networkPopUps/network.pop-up.factory';
import { BasePopUpData } from '../../networkPopUps/types';
import { MegaMenuAction } from './megaMenu/types';
import { TreeDetails } from '../../networkTree/types';
import { GridColumnConfiguration } from '@nirvana/shared/dtos/types/shared/grid';

export interface InitNetworkResponse {
  allColumns: Array<NetworkColumnDetails>;
  predefinedFilterKeys: Array<NetworkColumn>;
  filtersUiConfigs: Array<GridFilterUiConfiguration>;
}

export interface NetworkDrillDownData {
  externalParams?: NetworkGridAppExternalParams;
}

export interface InitNetworkData extends NetworkDrillDownData {
  config: InitNetworkResponse;
}

export interface NetworkGridAppExternalParams {
  filters?: Array<GridFilter<NetworkColumn>>;
  treeDetails?: TreeDetails;
  recursive?: boolean;
  displayedColumns?: Array<NetworkColumn>;
  columnConfigs?: Array<GridColumnConfiguration>;
  selectedViewId?: string;
}

//@ts-ignore
export type NetworkColumnDetails = ColumnDetails<NetworkColumn, NetworkColumn>;

export enum PaginationType {
  PAGINATION_TYPE_DEFAULT = 0,
  PAGINATION_TYPE_OPTIMIZED = 1,
}

export interface IntegrationTagFilter {
  type: number;
  attribute: string;
  valueTag: string;
}

export interface NetworkReadRecordsParams {
  page: number;
  limit: number;
  sort?: string;
  dir?: string; // The direction of the sort
  filter?: Array<any>;
  nodeId?: string;
  viewType?: number;
  additionalFilters?: AdditionalFilters;
  paginationType?: PaginationType;
}

export interface AdditionalFilters {
  depth: number;
  type?: Array<string>;
  managedNetworkV2?: number;
  unmanagedNetworkV2?: number;
  policyDirect?: number;
  policyInherited?: number;
  policyTemplate?: string;
  policyPowerUser?: number;
  withSecurityIssuesNetworkV2?: number;
  withoutSecurityIssuesNetworkV2?: number;
  isolated?: number;
  tagStore?: Array<IntegrationTagFilter>;
  roleNetworkV2?: Array<string>;
  inDeletedFolder?: Array<string>;
  migratedToAnotherCompany?: Array<string>;
}

export interface AdditionalFilterProps {
  depth: number;
  additionalFiltersFromGrid: GridFilter<NetworkColumn>[];
}

export enum NetworkColumn {
  COMPANY = 'company',
  NAME = 'name',
  FQDN = 'fqdn',
  ENDPOINT_TYPE = 'endpointType',
  OS_VERSION = 'osVersion',
  OS_TYPE = 'osType',
  IP = 'ip',
  LABEL = 'label',
  CONTAINER_ID = 'containerId',
  CONTAINER_NAME = 'containerName',
  LAST_SEEN = 'lastSeen',
  USERS = 'users',
  TAGS = 'tags',
  TYPE = 'type',
  POLICY = 'policy',
  POLICY_STATUS = 'policyStatus',
  MANAGEMENT_STATUS = 'managementStatus',
  PENDING_ACTIVITY = 'pendingActivity',
  POLICY_TYPE = 'policyType',
  SECURITY_ISSUES = 'securityIssues',
  ROLE = 'roleNetworkV2',
  POWER = 'power',
  AWS_INTEGRATION_TAGS = 'awsIntegrationTags',
  AVAILABLE_POLICIES = 'availablePolicies',
  MIGRATED_TO_ANOTHER_COMPANY = 'migratedToAnotherCompany',
  IN_DELETED_FOLDER = 'inDeletedFolder',
}

export enum SecurityIssuesValues {
  WITH_SECURITY_ISSUES = 'withSecurityIssuesNetworkV2',
  WITHOUT_SECURITY_ISSUES = 'withoutSecurityIssuesNetworkV2',
  ISOLATED = 'isolatedNetworkV2',
}

export enum EntityRole {
  ENDPOINT = 'endpoint',
  EXCHANGE_PROTECTION = 'exchangeProtection',
  RELAY = 'relay',
  SECURITY_SERVER = 'sva',
  CONTAINER_HOST = 'containerHost',
  PATCH_CACHING_SERVER = 'patchCachingServer',
  AD_INTEGRATOR = 'adIntegrator',
  AD_INTEGRATION = 'adIntegration',
}

export interface ProtectedEntitiesNetwork extends ProtectedEntity {
  specifics: SpecificsNetwork;
}

export interface SpecificsNetwork extends Specifics {
  gzTags?: Array<TagNetworkGrid>;
  serverHasNsxIntegration?: boolean;
}

export interface RealNodeProtectedEntity extends TreeProtectedEntity {
  id: string;
}

export interface TreeProtectedEntity extends ProtectedEntity {
  readOnly: boolean;
  realNode?: RealNodeProtectedEntity;
  hasMoreChildNodes?: boolean;
  page?: number;
}

export interface NodeChildrenResponse {
  page: number;
  realNode: ProtectedEntity;
  data: Array<TreeProtectedEntity>;
  hasMoreChildNodes: boolean;
}

export interface TagNetworkGrid {
  id: string;
  name: string;
}

export interface NetworkGridItem {
  _id: string; // this needs to be here for grid
  company?: string;
  name: string;
  fqdn?: string;
  endpointType?: boolean;
  osVersion?: string;
  osType?: number;
  ip?: string;
  label?: string;
  containerId?: string;
  containerName?: string;
  lastSeen: LastSeenItemPart;
  users?: EndpointUsers;
  tags?: Array<TagNetworkGrid>;
  type: number;
  policy?: PolicyNetworkItem;
  pendingActivity: PendingActivityData;
  dbRecord: ProtectedEntity;
  securityIssues?: SecurityIssuesData;
}

export interface LastSeenItemPart {
  specifics: {
    lastSync: string | null;
    isOnline?: boolean;
  };
}

export interface PolicyNetworkItem {
  specifics: {
    clientRules?: Array<ClientRule>;
    clientPolicy?: ClientPolicy;
    serverHasNsxIntegration?: boolean;
    powerUser?: boolean;
    directlyApplied?: boolean;
    inherited?: boolean;
  };
  type: number;
  assignedPolicy?: AssignedPolicy;
  activePolicy?: ActivePolicy;
  clientPolicy?: ClientPolicy;
}

export enum GlobalNetworkAction {
  DELETE_ENTITY = 'delete-entity',
}

export enum NetworkTreeAction {
  RENAME = 'rename',
  CREATE_COMPANY_FOLDER = 'create-company-folder',
  CREATE_NETWORK_FOLDER = 'create-network-folder',
  DELETE = 'tree-delete',
}

export type NetworkAction = MegaMenuAction | NetworkTreeAction | GlobalNetworkAction;

export interface PopUpTypeAndData {
  popupType: NetworkPopUpType;
  extraPopupData?: Partial<BasePopUpData<any>>;
}

export const NetworkActionsToPopupsMap: Map<NetworkAction, PopUpTypeAndData> = new Map<NetworkAction, PopUpTypeAndData>(
  [
    [MegaMenuAction.MALWARE_SCAN, { popupType: NetworkPopUpType.MALWARE_SCAN }],
    [
      MegaMenuAction.INSTALL_AGENT,
      {
        popupType: NetworkPopUpType.INSTALL_AGENT,
        extraPopupData: { shouldOnlyExecuteCustomSetup: true },
      },
    ],
    [
      MegaMenuAction.RECONFIGURE_AGENT,
      {
        popupType: NetworkPopUpType.RECONFIGURE_AGENT,
        extraPopupData: { shouldOnlyExecuteCustomSetup: true },
      },
    ],
    [MegaMenuAction.IOC_SCAN, { popupType: NetworkPopUpType.IOC_SCAN }],
    [
      MegaMenuAction.EXCHANGE_SCAN,
      {
        popupType: NetworkPopUpType.EXCHANGE_SCAN_TASK,
        extraPopupData: { shouldOnlyExecuteCustomSetup: false },
      },
    ],
    [MegaMenuAction.REPAIR_AGENT, { popupType: NetworkPopUpType.REPAIR_AGENT }],
    [
      MegaMenuAction.ISOLATE_ENDPOINT,
      {
        popupType: NetworkPopUpType.ISOLATE_ENDPOINT,
        extraPopupData: { shouldOnlyExecuteCustomSetup: false },
      },
    ],
    [
      MegaMenuAction.REMOVE_FROM_ISOLATION,
      {
        popupType: NetworkPopUpType.REMOVE_FROM_ISOLATION,
        extraPopupData: { shouldOnlyExecuteCustomSetup: false },
      },
    ],
    [
      MegaMenuAction.RESTART_TASK,
      {
        popupType: NetworkPopUpType.RESTART_TASK,
        extraPopupData: { shouldOnlyExecuteCustomSetup: false },
      },
    ],
    [
      MegaMenuAction.UNINSTALL_AGENT,
      {
        popupType: NetworkPopUpType.UNINSTALL_AGENT,
        extraPopupData: { shouldOnlyExecuteCustomSetup: false },
      },
    ],
    [MegaMenuAction.INSTALL_PATCHES, { popupType: NetworkPopUpType.INSTALL_PATCHES }],
    [MegaMenuAction.MARK_GOLDEN_IMAGE, { popupType: NetworkPopUpType.MARK_GOLDEN_IMAGE }],
    [MegaMenuAction.UNMARK_GOLDEN_IMAGE, { popupType: NetworkPopUpType.UNMARK_GOLDEN_IMAGE }],
    [
      MegaMenuAction.ASSIGN_TAGS,
      {
        popupType: NetworkPopUpType.TAGS_MANAGEMENT,
        extraPopupData: {
          shouldOnlyExecuteCustomSetup: true,
          specifics: { action: 'assign' },
        },
      },
    ],
    [
      MegaMenuAction.UNASSIGN_TAGS,
      {
        popupType: NetworkPopUpType.TAGS_MANAGEMENT,
        extraPopupData: {
          shouldOnlyExecuteCustomSetup: true,
          specifics: { action: 'unassign' },
        },
      },
    ],
    [
      MegaMenuAction.UPDATE_AGENT,
      {
        popupType: NetworkPopUpType.UPDATE_AGENT,
        extraPopupData: { shouldOnlyExecuteCustomSetup: false },
      },
    ],
    [
      GlobalNetworkAction.DELETE_ENTITY,
      {
        popupType: NetworkPopUpType.DELETE_ENTITY,
        extraPopupData: { shouldOnlyExecuteCustomSetup: false },
      },
    ],
    [MegaMenuAction.PATCH_SCAN, { popupType: NetworkPopUpType.PATCH_SCAN }],
    [MegaMenuAction.RESUME_INTEGRITY_MONITORING, { popupType: NetworkPopUpType.RESUME_INTEGRITY_MONITORING }],
    [MegaMenuAction.SUSPEND_INTEGRITY_MONITORING, { popupType: NetworkPopUpType.SUSPEND_INTEGRITY_MONITORING }],
    [MegaMenuAction.ASSIGN_POLICY, { popupType: NetworkPopUpType.ASSIGN_POLICY }],
    [MegaMenuAction.RUN_NETWORK_DISCOVERY, { popupType: NetworkPopUpType.RUN_NETWORK_DISCOVERY }],
    [MegaMenuAction.RISK_SCAN, { popupType: NetworkPopUpType.RISK_SCAN }],
    [MegaMenuAction.UPDATE_SECURITY_SERVER, { popupType: NetworkPopUpType.UPDATE_SECURITY_SERVER }],
    [
      MegaMenuAction.REMOTE_SHELL,
      {
        popupType: NetworkPopUpType.REMOTE_SHELL,
        extraPopupData: { shouldOnlyExecuteCustomSetup: true },
      },
    ],
    [
      MegaMenuAction.MOVE_TO,
      {
        popupType: NetworkPopUpType.MOVE_TO,
        extraPopupData: { shouldOnlyExecuteCustomSetup: false },
      },
    ],
    [MegaMenuAction.ASSIGN_SECURITY_SERVERS, { popupType: NetworkPopUpType.ASSIGN_SECURITY_SERVERS }],
    [
      MegaMenuAction.SUBMIT_TO_SANDBOX,
      {
        popupType: NetworkPopUpType.SUBMIT_TO_SANDBOX,
        extraPopupData: { shouldOnlyExecuteCustomSetup: true },
      },
    ],
    [MegaMenuAction.SUSPEND_PROTECTION, { popupType: NetworkPopUpType.SUSPEND_PROTECTION }],
    [MegaMenuAction.RESUME_PROTECTION, { popupType: NetworkPopUpType.RESUME_PROTECTION }],
    [MegaMenuAction.SET_AD_INTEGRATOR, { popupType: NetworkPopUpType.SET_AD_INTEGRATOR }],
    [MegaMenuAction.REMOVE_AD_INTEGRATOR, { popupType: NetworkPopUpType.REMOVE_AD_INTEGRATOR }],
    [MegaMenuAction.REMOVE_AD_INTEGRATION, { popupType: NetworkPopUpType.REMOVE_AD_INTEGRATION }],
    [MegaMenuAction.SYNC_WITH_EC2, { popupType: NetworkPopUpType.SYNC_WITH_EC2, extraPopupData: { shouldOnlyExecuteCustomSetup: true } }],
    [MegaMenuAction.ASSIGN_VCENTER_INTEGRATOR, { popupType: NetworkPopUpType.ASSIGN_VCENTER_INTEGRATOR }],
    [MegaMenuAction.UNASSIGN_VCENTER_INTEGRATOR, { popupType: NetworkPopUpType.UNASSIGN_VCENTER_INTEGRATOR }],
    [
      MegaMenuAction.SYNC_WITH_EC2,
      { popupType: NetworkPopUpType.SYNC_WITH_EC2, extraPopupData: { shouldOnlyExecuteCustomSetup: true } },
    ],
  ]
);

export interface ValidationHistoryMap {
  [action: string]: boolean;
}

export enum CompanyMethod {
  GET_COMPANY_BY_ID = 'getCompanyById',
  GET_AVAILABLE_COMPANIES = 'getAvailableCompanies',
}

export enum TreeFilters {
  COMPANIES_VIEW = 10,
}

export interface SecurityIssuesData {
  specifics: {
    isIsolated?: boolean;
    isManaged?: boolean;
  };
  hasSecurityIssues?: boolean;
}

export enum ManagementStatus {
  MANAGED_NETWORK_V2 = 'managedNetworkV2',
  UNMANAGED_NETWORK_V2 = 'unmanagedNetworkV2',
}

export enum PendingActivityValues {
  PENDING_RESTART = 'pendingRestart',
  PATCH_PENDING_RESTART = 'pendingRestartPatchReason',
  TROUBLESHOOTING_IN_PROGRESS = 'troubleshootingInProgress',
}

export interface PendingActivityData {
  pendingRestart?: boolean;
  pendingRestartPatch?: boolean;
  troubleshootingState?: number;
}

export enum IconLocation {
  GRID = 'grid',
  TREE = 'tree',
}

export enum GridIcon {
  ICON_CONTAINERS = 'ds-icon-containers',
  ICON_SECURITY_CONTAINER = 'ds-icon-security_container',
  ICON_CONTAINER_HOSTS = 'ds-icon-container_hosts',
  ICON_GOLDEN_IMAGE = 'ds-icon-golden_image',
  ICON_COMPANY = 'ds-icon-company',
  ICON_CUSTOMER = 'ds-icon-customer',
  ICON_RESOURCE_GENERIC = 'ds-icon-resource_generic',
  ICON_FOLDER = 'ds-icon-group',
  ICON_NETWORK = 'ds-icon-network',
  ICON_COMPANIES = 'ds-icon-companies',
  ICON_PHYSICAL_MACHINE = 'ds-icon-entity_endpoint',
  ICON_VIRTUAL_MACHINE = 'ds-icon-virtual_machines',
  ICON_SECURITY_SERVER = 'ds-icon-security_servers',
  ICON_AWS_GROUP = 'ds-icon-Amazon-EC2-group',
  ICON_AWS_ACCOUNT = 'ds-icon-integration-group',
  ICON_AWS_REGION = 'ds-icon-aws-regions',
  ICON_AWS_AZ = 'ds-icon-aws-availability-zones',
  ICON_AWS_INSTANCE = 'ds-icon-aws-instances',
  ICON_VMWARE_COLLECTION = 'ds-icon-vmware-collection',
  ICON_VMWARE_CENTER = 'ds-icon-vmware-center',
  ICON_VMWARE_DATACENTER = 'ds-icon-vmware-datacenter',
  ICON_VMWARE_CLUSTER_COMPUTE_RESOURCE = 'ds-icon-vmware-cluster-compute-resource',
  ICON_VMWARE_VIRTUAL_APP = 'ds-icon-vmware-virtual-app',
  ICON_VMWARE_HOST = 'ds-icon-vmware-host',
  ICON_VMWARE_INTEGRATOR = 'ds-icon-vmware-integrator',
}

export enum Events {
  LABEL_ROW_CLICKED = 'labelRowClicked',
  DELETE_ENTITIES_SUCCESS = 'deleteEntitiesSuccess',
  USER_COLUMN_CLICKED = 'userColumnClicked',
}

export enum NetworkV2Analytics {
  TRACKING_NAME = 'network-v2',
  IDENTIFIER_ACTION_CLICKED = 'action-clicked',
  IDENTIFIER_SMART_VIEW_CLICKED = 'smart-view-clicked',
  IDENTIFIER_NETWORK_TREE = 'network-tree',
  ASSET_TREE_SEARCH_USED = 'tree-search-used',
}

export enum DeleteActionState {
  DEFAULT_TARGETS_IN_SELECTED_ITEMS = 'default-targets-in-selected-items',
  CONTAINER_HOST_VM_ONLINE_IN_SELECTED_ITEMS = 'container-host-vm-online-in-selected-items',
  COMPANY_FOLDER_IN_SELECTED_ITEMS = 'company-folder-in-selected-items',
  NOT_EMPTY_COMPANY_FOLDER_IN_SELECTED_ITEMS = 'not-empty-company-folder-in-selected-items',
  EMPTY_COMPANY_FOLDER_IN_SELECTED_ITEMS = 'empty-company-folder-in-selected-items',
  CUSTOM_GROUP_IN_SELECTED_ITEMS = 'custom-group-in-selected-items',
  NOT_EMPTY_CUSTOM_GROUPS_FOLDER_IN_SELECTED_ITEMS = 'not-empty-custom-groups-folder-in-selected-items',
  EMPTY_CUSTOM_GROUPS_FOLDER_IN_SELECTED_ITEMS = 'empty-custom-groups-folder-in-selected-items',
  CONTAINER_GROUP_IN_SELECTED_ITEMS = 'container-group-in-selected-items',
  CONTAINER_GROUP_HAS_CONTAINER_HOSTS_IN_SELECT_ITEMS = 'not-empty-container-group-in-selected-items',
  EMPTY_CONTAINER_GROUP_IN_SELECTED_ITEMS = 'empty-container-group-in-selected-items',
  MANAGED_ENDPOINTS_IN_SELECTED_ITEMS = 'managed-endpoints-in-selected-items',
  UNMANAGED_ENDPOINTS_IN_SELECTED_ITEMS = 'unmanaged-endpoints-in-selected-items',
  NON_DELETABLE_AWS_ENTITIES = 'non-deletable-aws-entities',
  NON_DELETABLE_VMWARE_ENTITIES = 'non-deletable-vmware-entities',
  SVA_IN_SELECTED_ITEMS = 'sva-in-selected-items',
  ENDPOINTS_IN_EXCLUDED_FOLDER = 'endpoints-in-excluded-folder',
  UNMANAGED_ENDPOINTS_ONLY_IN_EXCLUDED_FOLDER = 'unmanaged-endpoints-in-excluded-folder',
  MANAGED_ENDPOINTS_ONLY_IN_EXCLUDED_FOLDER = 'managed-endpoints-in-excluded-folder',
  COMPANY_IN_SELECTED_ITEMS = 'company-in-selected-items',
  CONTAINER_HOST_VM_DELETABLE_IN_SELECTED_ITEMS = 'container-host-vm-deletable-in-selected-items',
  CONTAINER_IN_SELECTED_ITEMS = 'container-in-selected-items',
  SVA_ONLY_IN_EXCLUDED_FOLDER = 'sva-in-excluded-folder',
  CONTAINER_HOST_VM_PROTECTED_BY_SECURITY_CONTAINER_IN_SELECTED_ITEMS = 'container-host-vm-protected-by-security-container-in-selected-items',
  CONTAINER_HOST_FOLDER_IN_SELECTED_ITEMS = 'container-host-folder-in-selected-items',
  INTEGRATION_NON_INTEGRATION_ENTITIES = 'integration-non-integration-entities',
  AD_ENTITIES_IN_SELECTED_ITEMS = 'ad-entities-in-selected-items',
  AWS_ENTITIES_IN_SELECTED_ITEMS = 'aws-entities-in-selected-items',
}

export type NetworkActionErrorCode = DeleteActionState; // Join error codes here

// TODO @aoprea populate smart views here
export const SmartViewIdToAnalyticsNameMap: Map<string, string> = new Map<string, string>([
  ['668d49969d6cac7e3b2ddf6a', 'managed-endpoints'],
  ['668d49969d6cac7e3b2ddf6b', 'managed-exchange-servers'],
  ['668d49969d6cac7e3b2ddf6c', 'managed-relays'],
  ['668d49969d6cac7e3b2ddf6d', 'security-servers'],
  ['668d49969d6cac7e3b2ddf6e', 'container-hosts'],
  ['668d49969d6cac7e3b2ddf6f', 'unmanaged-endpoints'],
  ['668d49969d6cac7e3b2ddf70', 'patch-caching-servers'],
  ['668d49969d6cac7e3b2ddf71', 'with-security-issues'],
  ['668d49969d6cac7e3b2ddf72', 'without-security-issues'],
  ['668d49969d6cac7e3b2ddf73', 'isolated'],
  ['668d49969d6cac7e3b2ddf74', 'pending restart'],
  ['668d49969d6cac7e3b2ddf75', 'patch-pending-restart'],
  ['668d49969d6cac7e3b2ddf76', 'troubleshooting-in-progress'],
  ['668d49969d6cac7e3b2ddf77', 'entities-with-directly-applied-policy'],
  ['668d49969d6cac7e3b2ddf78', 'entities-with-inherited-policy'],
  ['668d49969d6cac7e3b2ddf79', 'entities-with-policy-edited-by-power-user'],
  ['668d49969d6cac7e3b2ddf7a', 'entities-with-active-policies'],
  ['668d49969d6cac7e3b2ddf7b', 'entities-with-applied-policies'],
  ['668d49969d6cac7e3b2ddf7c', 'entities-with-pending-policies'],
]);

export interface GuidedTourSetterResponse {
  alreadyIncreasedCounter: boolean;
}

export const NETWORK_GUIDED_TOUR_HTML_ELEMENTS_NEED_PREPARING = 5;

export const NETWORK_DATA_PROVIDER_ERROR = 'network-data-provider-error';
